import React, { useEffect, useMemo, useState } from "react";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Save, Users } from "lucide-react";
import { toast } from "sonner";
import {
    listPlugins,
    listPluginPrompts,
    createPluginPrompt,
} from "../lib/api";

/**
 * Per-plugin prompt boxes for the Analyze/Estimate dialogs (iter-43).
 *
 * For every active plugin, renders:
 *  - Header (plugin name)
 *  - List of the user's saved prompts for that plugin+kind with a
 *    checkbox to include this run, and a delete button.
 *  - A "Custom:" textarea for a one-off adhoc prompt.
 *  - A "Save" button that prompts for a title and POSTs to the backend.
 *
 * Controlled via ``value`` / ``onChange`` so the parent dialog owns the
 * aggregated state that goes into the submit payload:
 *   {
 *     plugin_prompt_ids: string[],      // saved prompt ids, checked
 *     plugin_adhoc_prompts: [{plugin_id, text}],
 *     plugin_prompt_inputs: {           // iter-61 — runtime fills
 *       [prompt_id]: { text?: str } | { selections?: string[] }
 *     }
 *   }
 */
export default function PluginPromptsPanel({
    activePluginIds = [],
    kind, // "estimate" | "analyze"
    value,
    onChange,
    testIdPrefix = "plugin-prompts",
}) {
    const [catalog, setCatalog] = useState([]);
    const [saved, setSaved] = useState([]); // flat list, filter per plugin
    const [loading, setLoading] = useState(false);

    const adhoc = value?.adhoc || {}; // { plugin_id: text }
    const checkedIds = value?.checkedIds || []; // string[]
    // iter-61: per-prompt runtime input state.
    const inputs = value?.inputs || {};

    const patch = (next) => {
        onChange && onChange({ adhoc, checkedIds, inputs, ...next });
    };

    useEffect(() => {
        let live = true;
        (async () => {
            setLoading(true);
            try {
                const [pl, prompts] = await Promise.all([
                    listPlugins(),
                    listPluginPrompts({ kind, includeShared: true }),
                ]);
                if (!live) return;
                setCatalog(pl || []);
                setSaved(prompts || []);
            } catch (e) {
                // Non-fatal — show empty state.
                if (live) setSaved([]);
            } finally {
                if (live) setLoading(false);
            }
        })();
        return () => {
            live = false;
        };
    }, [kind]);

    const pluginMeta = useMemo(() => {
        const byId = {};
        for (const p of catalog) byId[p.id] = p;
        return byId;
    }, [catalog]);

    const byPlugin = useMemo(() => {
        const out = {};
        for (const p of saved) {
            if (!out[p.plugin_id]) out[p.plugin_id] = [];
            out[p.plugin_id].push(p);
        }
        return out;
    }, [saved]);

    // A "shared" prompt has a non-"none" permanent_scope — it's visible
    // to every user as a selectable toggle, but it is NEVER auto-applied.
    // The user still clicks to turn it on per-run.
    const isShared = (p) => {
        const scope = p.permanent_scope || "none";
        return scope === "both" || scope === kind;
    };

    if (!activePluginIds || activePluginIds.length === 0) {
        return null;
    }

    const toggleCheck = (id) => {
        const next = checkedIds.includes(id)
            ? checkedIds.filter((x) => x !== id)
            : [...checkedIds, id];
        patch({ checkedIds: next });
    };

    const setAdhoc = (pluginId, text) => {
        patch({ adhoc: { ...adhoc, [pluginId]: text } });
    };

    const handleSave = async (pluginId) => {
        const body = (adhoc[pluginId] || "").trim();
        if (!body) {
            toast.error("Type a prompt first.");
            return;
        }
        // eslint-disable-next-line no-alert
        const title = window.prompt(
            "Save this prompt as… (title)",
            `My ${pluginMeta[pluginId]?.name || pluginId} prompt`,
        );
        if (title === null) return;
        try {
            const doc = await createPluginPrompt({
                plugin_id: pluginId,
                kind,
                title: (title || "Untitled prompt").trim(),
                body,
            });
            setSaved((prev) => [...prev, doc]);
            // auto-check the newly saved prompt AND clear the adhoc box
            patch({
                checkedIds: [...checkedIds, doc.id],
                adhoc: { ...adhoc, [pluginId]: "" },
            });
            toast.success("Prompt saved");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || e?.message || "Couldn't save prompt",
            );
        }
    };

    return (
        <section className="space-y-3" data-testid={`${testIdPrefix}-panel`}>
            {activePluginIds.map((pid) => {
                const meta = pluginMeta[pid];
                const savedForPlugin = byPlugin[pid] || [];
                const accent = meta?.accent || "#00A3FF";
                return (
                    <div
                        key={pid}
                        data-testid={`${testIdPrefix}-plugin-${pid}`}
                        className="rounded-sm border border-[#2A3655] bg-[#0A1128] p-3 space-y-2.5"
                        style={{ borderLeft: `3px solid ${accent}` }}
                    >
                        <div className="flex items-center gap-2">
                            <span
                                className="text-sm font-bold uppercase tracking-wide"
                                style={{ color: accent }}
                            >
                                {meta?.name || pid}
                            </span>
                            {savedForPlugin.length > 0 && (
                                <span className="text-[10px] text-slate-500">
                                    {savedForPlugin.length} saved
                                </span>
                            )}
                        </div>

                        {/* iter-48: Saved prompts are toggle pills —
                            user's own + any shared ones (permanent_scope
                            != "none"). Shared prompts get a small user
                            icon so users can tell which are public. No
                            prompt is ever auto-applied — it only fires
                            when the user toggles it green. */}
                        {savedForPlugin.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <div className="flex flex-wrap gap-1.5">
                                    {savedForPlugin.map((pr) => {
                                        const shared = isShared(pr);
                                        const checked = checkedIds.includes(pr.id);
                                        return (
                                            <button
                                                key={pr.id}
                                                type="button"
                                                onClick={() => toggleCheck(pr.id)}
                                                data-testid={`${testIdPrefix}-toggle-${pr.id}`}
                                                data-active={checked ? "true" : "false"}
                                                data-shared={shared ? "true" : "false"}
                                                className={`text-[12px] font-semibold px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                                                    checked
                                                        ? "bg-emerald-500/20 border-emerald-400 text-emerald-200 shadow-[0_0_0_2px_rgba(52,211,153,0.2)]"
                                                        : "bg-[#0F1B3A] border-[#2A3655] text-slate-300 hover:border-slate-500 hover:text-white"
                                                }`}
                                                title={
                                                    shared
                                                        ? `Shared prompt — available to all users. Click to include in this ${kind}.\n\n${pr.body}`
                                                        : pr.body
                                                }
                                            >
                                                {shared && (
                                                    <Users className="w-2.5 h-2.5 opacity-70" />
                                                )}
                                                <span>{pr.title || "Untitled"}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {/* iter-61: inline inputs for any checked
                                    text / multi_select prompts. */}
                                {savedForPlugin
                                    .filter(
                                        (pr) =>
                                            checkedIds.includes(pr.id) &&
                                            (pr.input_type || "button") !== "button",
                                    )
                                    .map((pr) => (
                                        <PromptInputControls
                                            key={`in-${pr.id}`}
                                            prompt={pr}
                                            value={inputs[pr.id]}
                                            onChange={(next) =>
                                                patch({
                                                    inputs: {
                                                        ...inputs,
                                                        [pr.id]: next,
                                                    },
                                                })
                                            }
                                            testId={`${testIdPrefix}-input-${pr.id}`}
                                        />
                                    ))}
                            </div>
                        )}

                        {/* Adhoc custom box */}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                                    Custom:
                                </label>
                            </div>
                            <Textarea
                                rows={3}
                                value={adhoc[pid] || ""}
                                onChange={(e) => setAdhoc(pid, e.target.value)}
                                placeholder={`Add a custom ${kind} prompt for ${
                                    meta?.name || pid
                                }… (e.g. "focus on leaching bed sizing and gravel depth")`}
                                data-testid={`${testIdPrefix}-adhoc-${pid}`}
                                className="bg-[#0F1B3A] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF] text-[13px]"
                            />
                            <div className="flex items-center justify-end mt-1.5">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleSave(pid)}
                                    data-testid={`${testIdPrefix}-save-${pid}`}
                                    className="text-[11px] h-7 px-2 text-[#00A3FF] hover:text-white hover:bg-[#00A3FF]/20"
                                >
                                    <Save className="w-3 h-3 mr-1" />
                                    Save
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </section>
    );
}

/* --------------------------------------------------------------------------
 * iter-61: runtime input controls for text / multi_select prompts.
 * Rendered only when the user has toggled the prompt on. The value is
 * lifted to the parent dialog via `onChange` so the aggregated
 * `plugin_prompt_inputs` payload reaches the AI job request.
 * ------------------------------------------------------------------------ */
function PromptInputControls({ prompt, value, onChange, testId }) {
    const inputType = prompt.input_type || "button";
    if (inputType === "text") {
        return (
            <div
                className="rounded-sm border border-emerald-500/30 bg-emerald-500/[0.04] p-2 space-y-1"
                data-testid={testId}
            >
                <label className="text-[10px] uppercase tracking-wider text-emerald-200 font-bold flex items-center gap-1.5">
                    {prompt.title || "Input"}
                </label>
                <input
                    type="text"
                    value={(value && value.text) || ""}
                    onChange={(e) =>
                        onChange({ text: e.target.value })
                    }
                    placeholder={prompt.placeholder || "Type your input…"}
                    data-testid={`${testId}-field`}
                    className="w-full bg-[#0A1128] border border-[#2A3655] rounded-sm px-2 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400"
                />
            </div>
        );
    }
    if (inputType === "multi_select") {
        const options = prompt.options || [];
        const selections = (value && value.selections) || [];
        const toggle = (id) => {
            const next = selections.includes(id)
                ? selections.filter((x) => x !== id)
                : [...selections, id];
            onChange({ selections: next });
        };
        return (
            <div
                className="rounded-sm border border-emerald-500/30 bg-emerald-500/[0.04] p-2 space-y-1.5"
                data-testid={testId}
            >
                <label className="text-[10px] uppercase tracking-wider text-emerald-200 font-bold">
                    {prompt.title || "Select options"}
                </label>
                <div className="flex flex-wrap gap-1.5">
                    {options.length === 0 && (
                        <span className="text-[11px] text-slate-500 italic">
                            (no options defined)
                        </span>
                    )}
                    {options.map((o) => {
                        const on = selections.includes(o.id);
                        return (
                            <button
                                key={o.id}
                                type="button"
                                onClick={() => toggle(o.id)}
                                data-testid={`${testId}-option-${o.id}`}
                                data-active={on ? "true" : "false"}
                                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                                    on
                                        ? "bg-emerald-500/30 border-emerald-400 text-white"
                                        : "bg-[#0A1128] border-[#2A3655] text-slate-300 hover:border-slate-500"
                                }`}
                            >
                                {on ? "✓ " : ""}
                                {o.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }
    return null;
}
